import arrowUpRightIcon from "@tabler/icons/outline/arrow-up-right.svg";
import pinnedIcon from "@tabler/icons/outline/pinned.svg";
import repeatIcon from "@tabler/icons/outline/repeat.svg";
import rocketIcon from "@tabler/icons/outline/rocket.svg";
import React, { useEffect, useRef, useState } from "react";
import { useIntl, FormattedMessage, defineMessages } from "react-intl";
import { Link, useNavigate, useLocation } from "react-router-dom";

import { useMetrics } from "@/api/ads";
import { useBoostImpression } from "@/api/boosts";
import AccountContainer from "@/containers/account-container";
import { useAuth } from "@/contexts/auth/useAuth";
import { SurfaceProvider, type SurfaceKey } from "@/contexts/surface-context";
import NewsEmbedCard from "@/features/sports-news/news-embed-card";
import EmbeddedAnnouncement from "@/features/status/components/embedded-announcement";
import QuotedStatus from "@/features/status/containers/quoted-status-container";
import { useTailwindBreakpoint } from "@/hooks";
import {
  statusTrackingTarget,
  useStatusTracking,
} from "@/hooks/useStatusTracking";
import { cn } from "@/utils/cn";
import {
  defaultMediaVisibility,
  textForScreenReader,
  getActualStatus,
} from "@/utils/status";

import Poll from "./polls/poll";
import Scorecard from "./scorecard";
import StatusActionBar from "./status-action-bar";
import StatusContent from "./status-content";
import StatusMedia from "./status-media";
import StatusReplyMentions from "./status-reply-mentions";
import GroupHeader from "./statuses/header/group";
import SensitiveContentOverlay from "./statuses/sensitive-content-overlay";
import StatusInfo from "./statuses/status-info";
import StatusMenu from "./statuses/status-menu";
import SuppressedStatus from "./statuses/suppressed-status";
import Tombstone from "./tombstone";
import { Button, Card, Icon, Stack, Text } from "./ui";

import type { Status as StatusEntity } from "@/schemas";

// Defined in components/scrollable-list
export type ScrollPosition = { height: number; top: number };

const messages = defineMessages({
  reblogged_by: {
    id: "status.reblogged_by",
    defaultMessage: "{name} ReTruthed",
  },
  boostedBadgeDefault: {
    id: "boost.badge.default",
    defaultMessage: "Boosted Truth",
  },
  boostedBadgeScreenReaderPrefix: {
    id: "boost.badge.screen_reader_prefix",
    defaultMessage: "Boosted Truth:",
  },
});

export interface StatusProps {
  accountAction?: React.ReactElement;
  avatarSize?: number;
  contextType?: string;
  featured?: boolean;
  focusable?: boolean;
  hidden?: boolean;
  hideActionBar?: boolean;
  hideHeader?: boolean;
  hoverable?: boolean;
  id?: string;
  /** Is the Status part of the "For You" feed? If so, add new options to Options dropdown. */
  isRecommended?: boolean;
  muted?: boolean;
  onClick?: () => void;
  showGroup?: boolean;
  source?: string;
  status: StatusEntity;
  /** Explicit impression surface (thread rows); feed lists use <SurfaceProvider> instead. */
  trackingSurface?: SurfaceKey;
  unread?: boolean;
  variant?: "default" | "rounded" | "slim";
  searchedText?: string;
  isThreadStatus?: boolean;
  /** Render a non-interactive preview: no action bar, no translation, no
   *  navigation/focus. Used for decorative surfaces like the landing page. */
  preview?: boolean;
}

const Status: React.FC<StatusProps> = (props) => {
  const {
    accountAction,
    avatarSize = 42,
    contextType,
    featured,
    focusable = true,
    hidden,
    hideActionBar,
    hideHeader,
    hoverable = true,
    isRecommended = false,
    muted,
    onClick,
    showGroup = true,
    source,
    status,
    variant = "default",
    unread,
    searchedText,
    isThreadStatus,
    preview = false,
    trackingSurface,
  } = props;

  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentAccount } = useAuth();
  const breakpoint = useTailwindBreakpoint();
  const isMobile = breakpoint === "sm";

  const displayMedia = currentAccount?.settings?.displayMedia as string;
  const didShowCard = useRef(false);
  const node = useRef<HTMLDivElement>(null);
  const overlay = useRef<HTMLDivElement>(null);

  const [showMedia, setShowMedia] = useState<boolean>(
    defaultMediaVisibility(status, displayMedia),
  );
  const [minHeight, setMinHeight] = useState(208);

  const actualStatus = getActualStatus(status);

  const isReblog = status.reblog && typeof status.reblog === "object";
  const statusUrl = `/@${actualStatus.account.acct}/posts/${actualStatus.id}`;
  const group = actualStatus.group;
  const prediction = status.in_reply_to?.prediction;

  void useMetrics(actualStatus.metrics, source);
  useBoostImpression(status.boost_id);

  useStatusTracking(
    node,
    statusTrackingTarget(status),
    trackingSurface ? { surface: trackingSurface } : undefined,
  );

  const [isTombstoned, setIsTombstoned] = useState(!!status.tombstone?.reason);

  const isUsingProfilePage = !!location.pathname.match(/@\w+$/gm);

  useEffect(() => {
    didShowCard.current = Boolean(!muted && !hidden && status?.card);
  }, []);

  useEffect(() => {
    setShowMedia(defaultMediaVisibility(status, displayMedia));
  }, [status.id]);

  useEffect(() => {
    if (overlay.current) {
      setMinHeight(overlay.current.getBoundingClientRect().height);
    }
  }, [overlay.current]);

  const handleToggleMediaVisibility = (): void => {
    setShowMedia(!showMedia);
  };

  const handleClick = (e?: React.MouseEvent): void => {
    e?.stopPropagation();

    // If the user is selecting text, don't focus the status.
    if (getSelection()?.toString().length) {
      return;
    }

    if (!e || !(e.ctrlKey || e.metaKey)) {
      if (onClick) {
        onClick();
      } else {
        if (actualStatus.prediction) {
          navigate(`/predictions/${actualStatus.id}`);
        } else {
          navigate(statusUrl);
        }
      }
    } else {
      window.open(statusUrl, "_blank");
    }
  };

  const renderStatusInfo = () => {
    if (isReblog) {
      return (
        <StatusInfo
          avatarSize={avatarSize}
          icon={
            <Icon
              src={repeatIcon}
              className="size-4 text-success-600 dark:text-accent-green"
            />
          }
          text={
            <FormattedMessage
              id="status.reblogged_by"
              defaultMessage="{name} ReTruthed"
              values={{
                name: (
                  <Link
                    to={`/@${status.account.acct}`}
                    className="hover:underline"
                  >
                    <bdi className="truncate">
                      <strong
                        className="text-gray-800 dark:text-gray-200"
                        dangerouslySetInnerHTML={{
                          __html: status.account.display_name_html,
                        }}
                      />
                    </bdi>
                  </Link>
                ),
              }}
            />
          }
        />
      );
    } else if (featured) {
      return (
        <StatusInfo
          avatarSize={avatarSize}
          icon={
            <Icon
              src={pinnedIcon}
              className="size-4 text-gray-600 dark:text-gray-400"
            />
          }
          text={
            <FormattedMessage
              id="status.pinned"
              defaultMessage="Pinned Truth"
            />
          }
        />
      );
    } else if (actualStatus.sponsored) {
      return (
        <StatusInfo
          avatarSize={avatarSize}
          icon={
            <Icon
              src={arrowUpRightIcon}
              className="size-4 text-gray-600 dark:text-gray-400"
            />
          }
          text={
            <Text tag="span" size="xs" theme="muted" weight="medium">
              <FormattedMessage
                id="status.sponsored"
                defaultMessage="Sponsored Truth"
              />
            </Text>
          }
        />
      );
    }
  };

  // Boost is rendered alongside (not instead of) renderStatusInfo so that a
  // ReTruth of a boosted post still gets its "X ReTruthed" header AND the
  // boost attribution. Uses the same StatusInfo primitive for visual
  // consistency with reblog/featured/sponsored rows.
  const renderBoostInfo = () => {
    if (!status.boosted) return null;
    return (
      <StatusInfo
        avatarSize={avatarSize}
        icon={
          <Icon
            src={rocketIcon}
            className="size-4 text-gray-600 dark:text-gray-400"
          />
        }
        text={
          <Text
            tag="span"
            size="xs"
            theme="muted"
            weight="medium"
            data-testid="boost-status-badge"
          >
            {intl.formatMessage(messages.boostedBadgeDefault)}
          </Text>
        }
      />
    );
  };

  if (!status) return null;

  if (hidden) {
    return (
      <div ref={node}>
        <>
          {actualStatus.account.display_name || actualStatus.account.username}
          {actualStatus.content}
        </>
      </div>
    );
  }

  let rebloggedByText;
  if (status.reblog && typeof status.reblog === "object") {
    rebloggedByText = intl.formatMessage(messages.reblogged_by, {
      name: status.account.acct,
    });
  }

  const boostedText = status.boosted
    ? `${intl.formatMessage(messages.boostedBadgeScreenReaderPrefix)} ${intl.formatMessage(messages.boostedBadgeDefault)}`
    : undefined;

  let quote;

  if (actualStatus.quote) {
    if (actualStatus.pleroma?.quote_visible === false) {
      quote = (
        <div className="quoted-status-tombstone rounded-2xl border p-4">
          <p>
            <FormattedMessage
              id="statuses.quote_tombstone"
              defaultMessage="Truth is unavailable."
            />
          </p>
        </div>
      );
    } else {
      quote = (
        <QuotedStatus
          avatarSize={avatarSize}
          statusId={actualStatus.quote.id}
          source={source}
        />
      );
    }
  }

  const isUnderReview = actualStatus.visibility === "self";
  const isSensitive = actualStatus.sensitive;
  const isStatusMuted = status.tombstone?.reason === "muted";
  const hasPoll = !!actualStatus.poll;
  const pollId =
    typeof actualStatus.poll === "string"
      ? actualStatus.poll
      : (actualStatus.poll?.id as string);

  const headerAction =
    accountAction || isMobile ? (
      <StatusMenu status={status} layout="timeline" isExpandable={false} />
    ) : undefined;

  if (isTombstoned && !isUsingProfilePage) {
    return (
      <Tombstone
        id={status.id}
        contextType={contextType}
        type={status.tombstone?.reason}
        action={
          isStatusMuted ? (
            <Button
              type="button"
              variant="link"
              className="p-0!"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsTombstoned(false);
              }}
            >
              <FormattedMessage id="common.view" defaultMessage="View" />
            </Button>
          ) : undefined
        }
      />
    );
  }

  // Show suppressed status component if the user has expressed
  // no interest in seeing this status's account/group AND
  // the status is coming from the For You feed.
  if (!status.interested && source === "recommended") {
    return <SuppressedStatus status={status} />;
  }

  return (
    <div
      className={cn(
        /* Add margin to avoid Edge auto-hiding scrollbar appearing over content.
             On Edge 16 this is 16px and Edge <=15 it's 12px, so aim for 16px. */
        "min-h-[54px] supports-[-ms-overflow-style:-ms-autohiding-scrollbar]:pr-[26px]",
        // `relative` is the positioning context for absolutely-positioned
        // children (boost badge, sensitive-content overlay) — keep it.
        "relative animate-fade-150 opacity-100",
        {
          "cursor-pointer": !preview,
          focusable: focusable && !preview,
        },
      )}
      tabIndex={focusable && !muted && !preview ? 0 : undefined}
      data-featured={featured ? "true" : null}
      aria-label={textForScreenReader(
        intl,
        actualStatus,
        rebloggedByText,
        boostedText,
      )}
      ref={node}
      onClick={preview ? undefined : handleClick}
      role={preview ? undefined : "link"}
      data-testid="status"
      id={`status-${status.id}`}
    >
      <Card
        variant={variant}
        className={cn("space-y-4", `status-${actualStatus.visibility}`, {
          "status-reply": !!status.in_reply_to_id,
          muted,
          read: unread === false,
          "p-4": variant === "default",
          "p-0 shadow-none": isThreadStatus,
        })}
        data-id={status.id}
      >
        {!hideHeader && (
          <>
            {renderBoostInfo()}
            {renderStatusInfo()}
            {(!isReblog || (isReblog && !actualStatus.prediction)) && (
              <>
                {showGroup && group ? (
                  <GroupHeader
                    status={actualStatus as any}
                    group={group}
                    account={actualStatus.account}
                    timestamp={actualStatus.created_at}
                    timestampUrl={statusUrl}
                    avatarSize={avatarSize}
                    action={headerAction}
                  />
                ) : (
                  <AccountContainer
                    key={actualStatus.account.id}
                    id={actualStatus.account.id}
                    timestamp={actualStatus.created_at}
                    timestampUrl={statusUrl}
                    editedAt={actualStatus.edited_at}
                    action={headerAction}
                    hideActions={!headerAction}
                    showProfileHoverCard={hoverable}
                    withLinkToProfile={hoverable}
                    avatarSize={avatarSize}
                  />
                )}
              </>
            )}
          </>
        )}

        <div
          className={cn({
            "pl-[54px] rtl:pr-[54px] rtl:pl-0": isThreadStatus,
          })}
        >
          <StatusReplyMentions status={actualStatus} hoverable={hoverable} />

          <Stack
            className="relative z-0"
            style={{
              minHeight:
                isUnderReview || isSensitive
                  ? Math.max(minHeight, 208) + 12
                  : undefined,
            }}
          >
            {(isUnderReview || isSensitive) && (
              <SensitiveContentOverlay
                status={status}
                visible={showMedia}
                onToggleVisibility={handleToggleMediaVisibility}
                ref={overlay}
              />
            )}

            <Stack space={4}>
              <StatusContent
                status={actualStatus}
                onClick={preview ? undefined : handleClick}
                collapsable={!preview && !isThreadStatus}
                hideTranslation={preview}
                searchedText={searchedText}
              />

              {(quote ||
                actualStatus.card ||
                actualStatus.media_attachments.length > 0) && (
                  <Stack space={4}>
                    <StatusMedia
                      status={actualStatus}
                      muted={muted}
                      onClick={handleClick}
                      showMedia={showMedia}
                      onToggleVisibility={handleToggleMediaVisibility}
                    />

                    {quote}
                  </Stack>
                )}

              {hasPoll && <Poll id={pollId} key="poll" status={status} />}

              {actualStatus.sports_game && (
                <Link
                  to={new URL(actualStatus.sports_game.url).pathname}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Scorecard event={actualStatus.sports_game} variant="embed" />
                </Link>
              )}

              {actualStatus.news_article && (
                <Link
                  to={`/sports/news/${actualStatus.news_article.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <NewsEmbedCard article={actualStatus.news_article} />
                </Link>
              )}

              {actualStatus.embedded_announcement && !preview && (
                <EmbeddedAnnouncement
                  announcement={actualStatus.embedded_announcement}
                />
              )}
            </Stack>
          </Stack>

          {prediction && (
            <div className="mt-4 border-y border-y-border py-2">
              <Text size="sm" theme="muted">
                {prediction.title}
              </Text>
            </div>
          )}

          {!hideActionBar && !preview && !isUnderReview && (
            <div className="pt-4">
              {/* Thread rows have no ambient <SurfaceProvider> so we are
                  wrapping just the action bar so engagement capture still resolves a surface. 
                  Feed rows already sit inside the feed
                  screen's own SurfaceProvider, so no extra wrap is needed there. */}
              {trackingSurface ? (
                <SurfaceProvider surface={trackingSurface}>
                  <StatusActionBar
                    status={actualStatus}
                    isRecommended={isRecommended}
                    layout="timeline"
                    excludeMenu={isMobile}
                  />
                </SurfaceProvider>
              ) : (
                <StatusActionBar
                  status={actualStatus}
                  isRecommended={isRecommended}
                  layout="timeline"
                  excludeMenu={isMobile}
                />
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Status;
